import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Form,
  Input,
  Label,
  Button,
  FormGroup,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";

// ===== shared imports (per your project rules) =====
import {
  _Get,
  _PostSave,
  _Put,
  ViewCompanyDetails,
  CreateCustomerList,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Create_Account_List,
  Create_Payment,
  Create_Transporter_List,
  Update_payment_By_Id2,
  View_Expense_Account,
  View_Receipt_By_Id,
  View_Company_Details,
} from "../import/ApiEndPoint/Api";

// ---- tiny CSS injected once ----
const injectOnce = () => {
  if (document.getElementById("create-payment-inline-css")) return;
  const style = document.createElement("style");
  style.id = "create-payment-inline-css";
  style.innerHTML = `
    :root{
      --bd:#e5e7eb;
      --bg:#f4f7f6;
      --card:#ffffff;
      --txt:#111827;
      --muted:#6b7280;
      --pri: rgb(8, 91, 245);
      --danger:#dc2626;
      --warn:#92400e;
      --warnBg:#fffbeb;
      --warnBd:#fcd34d;
    }

    .cp-wrap{ width:100%; }

    .cp-card{
      width:100%;
      border:1px solid var(--bd);
      border-radius:14px;
      overflow:visible;
      background:var(--card);
    }

    .cp-toolbar{
      display:flex;
      gap:12px;
      align-items:center;
      flex-wrap:wrap;
      justify-content:space-between;
      padding:10px 12px;
      border-bottom:1px solid var(--bd);
      background:var(--bg);
      position:sticky;
      top:0;
      z-index:10;
    }

    .cp-title{
      font-weight:900;
      text-transform:uppercase;
      font-size:18px;
      letter-spacing:.02em;
      color:var(--txt);
    }

    .cp-actions{
      display:flex;
      gap:8px;
      flex-wrap:wrap;
      align-items:center;
    }

    .cp-form label{
      font-size:12px;
      font-weight:700;
      color:var(--txt);
      margin-bottom:4px;
    }

    .cp-form input,
    .cp-form select{
      height:36px;
      font-size:13px;
      border-radius:10px;
      border:1px solid var(--bd);
    }

    .cp-form textarea{
      min-height:36px;
      font-size:13px;
      border-radius:10px;
      border:1px solid var(--bd);
      resize:none !important;
      overflow:hidden !important;
      line-height:1.45;
      padding-top:8px;
      padding-bottom:8px;
    }

    .cp-amount::-webkit-outer-spin-button,
    .cp-amount::-webkit-inner-spin-button{
      -webkit-appearance:none;
      margin:0;
    }

    .cp-amount{
      -moz-appearance:textfield;
      appearance:textfield;
    }

    .cp-line{
      width:100%;
      display:grid;
      gap:10px;
      align-items:end;
      grid-template-columns:
        170px
        minmax(260px, 1.5fr)
        140px
        220px
        110px
        minmax(420px, 2.6fr)
        38px;
    }
    .cp-line.cp-line-bulk{
      grid-template-columns:repeat(auto-fit, minmax(180px, 1fr));
    }

    .cp-xbtn{
      width:36px !important;
      height:36px !important;
      padding:0 !important;
      border-radius:10px !important;
      border:1px solid var(--bd) !important;
      background:#fff !important;
      color:var(--danger) !important;
      font-weight:900 !important;
      line-height:1 !important;
      display:inline-grid !important;
      place-items:center !important;
      cursor:pointer !important;
    }

    .cp-xbtn:hover{
      border-color:var(--danger) !important;
      box-shadow:0 6px 16px rgba(220,38,38,.12);
    }

    .cp-xbtn:disabled{
      opacity:.45 !important;
      cursor:not-allowed !important;
      border-color:var(--bd) !important;
      box-shadow:none !important;
    }

    .cp-sep{
      height:1px;
      background:#eef2ff;
      margin:12px 0;
    }

    .cp-note{
      margin-top:6px;
      font-size:11px;
      color:var(--warn);
      background:var(--warnBg);
      border:1px solid var(--warnBd);
      border-radius:8px;
      padding:6px 8px;
      line-height:1.35;
    }

    .cp-import-note{
      margin:0 0 12px 0;
      padding:10px 12px;
      border-radius:10px;
      background:#eff6ff;
      border:1px solid #bfdbfe;
      color:#1d4ed8;
      font-size:13px;
      font-weight:600;
    }

    @media (max-width: 1450px){
      .cp-line{
        grid-template-columns: 170px minmax(220px,1.2fr) 135px 180px 100px minmax(320px,2fr) 38px;
      }
    }

    @media (max-width: 1100px){
      .cp-line{
        grid-template-columns: 1fr 1fr;
      }
      .cp-line .cp-xbtn{
        width:100% !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// helpers
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");
const notEmpty = (v) =>
  v !== undefined && v !== null && String(v).trim() !== "";

const CASH_ID = "CASH";
const CASH_PARTY_SID = "CASH7845";

const bankLabel = (b) => {
  if (!b) return "Bank";
  const name = b?.bankName || "Bank";
  const acc = b?.accountNumber ? ` (${b.accountNumber})` : "";
  return `${name}${acc}`;
};

const getFinancialYearLabel = (dateValue = new Date()) => {
  let year;
  let month;

  if (typeof dateValue === "string" && dateValue.includes("-")) {
    const [yyyy, mm] = dateValue.split("-");
    year = Number(yyyy);
    month = Number(mm);
  } else {
    const d = new Date(dateValue);
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  if (!year || !month) {
    const d = new Date();
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const uniqueById = (arr) => {
  const out = [];
  const seen = new Set();
  (arr || []).forEach((x) => {
    const id = String(x?._id || "");
    if (!id) return;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(x);
  });
  return out;
};

const toDDMMYYYY = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return "";
  const [y, m, d] = String(iso).split("-");
  return `${d}-${m}-${y}`;
};

const normalizeToISODate = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  let m = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (m) {
    const dd = String(m[1]).padStart(2, "0");
    const mm = String(m[2]).padStart(2, "0");
    const yyyy = String(m[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  m = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/);
  if (m) {
    const yyyy = String(m[1]);
    const mm = String(m[2]).padStart(2, "0");
    const dd = String(m[3]).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
};

const monthMap = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
};

const normalizeMonthNameDateToDDMMYYYY = (input) => {
  const s = String(input || "")
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, " ");
  if (!s) return "";

  let m = s.match(/^(\d{1,2})[\s\-\/\.]+([A-Za-z]+)[\s\-\/\.]+(\d{4})$/);
  if (m) {
    const dd = String(m[1]).padStart(2, "0");
    const mm = monthMap[String(m[2]).toLowerCase()] || "";
    const yyyy = String(m[3]);
    if (mm) return `${dd}-${mm}-${yyyy}`;
  }

  m = s.match(/^([A-Za-z]+)[\s\-\/\.]+(\d{1,2})[\s\-\/\.]+(\d{4})$/);
  if (m) {
    const mm = monthMap[String(m[1]).toLowerCase()] || "";
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = String(m[3]);
    if (mm) return `${dd}-${mm}-${yyyy}`;
  }

  return "";
};

const normalizeDateInputToDDMMYYYY = (val) => {
  const iso = normalizeToISODate(val);
  if (iso) return toDDMMYYYY(iso);

  const s = String(val || "").trim();
  if (!s) return "";

  const fromMonthName = normalizeMonthNameDateToDDMMYYYY(s);
  if (fromMonthName) return fromMonthName;

  return s;
};

const ddmmyyyyToISO = (ddmmyyyy) => {
  const s = String(ddmmyyyy || "").trim();
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const excelSerialToDDMMYYYY = (serial) => {
  if (serial === undefined || serial === null || serial === "") return "";
  const n = Number(serial);
  if (!Number.isFinite(n)) return "";

  const date = new Date(Math.round((n - 25569) * 86400 * 1000));
  if (Number.isNaN(date.getTime())) return "";

  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const normalizeAnyExcelDateToDDMMYYYY = (value) => {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "number") {
    return excelSerialToDDMMYYYY(value);
  }

  if (value instanceof Date && !isNaN(value)) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  if (typeof value === "object" && value?.w) {
    return normalizeDateInputToDDMMYYYY(value.w);
  }

  const s = String(value).trim();
  if (!s) return "";

  if (/^\d+(\.\d+)?$/.test(s)) {
    const fromSerial = excelSerialToDDMMYYYY(Number(s));
    if (fromSerial) return fromSerial;
  }

  const normalized = normalizeDateInputToDDMMYYYY(s);
  if (/^\d{2}-\d{2}-\d{4}$/.test(normalized)) return normalized;

  const nativeParsed = new Date(s);
  if (!isNaN(nativeParsed.getTime())) {
    const dd = String(nativeParsed.getDate()).padStart(2, "0");
    const mm = String(nativeParsed.getMonth() + 1).padStart(2, "0");
    const yyyy = nativeParsed.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  return s;
};

// ✅ bank usage persistence
const usageKeyFor = (db) => `PAYMENT_BANK_USAGE_${String(db || "")}`;
const readUsage = (db) => {
  try {
    const raw = localStorage.getItem(usageKeyFor(db));
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
};
const bumpUsage = (db, bankId) => {
  if (!db || !bankId) return;
  if (String(bankId) === CASH_ID) return;
  const map = readUsage(db);
  const k = String(bankId);
  map[k] = (Number(map[k]) || 0) + 1;
  localStorage.setItem(usageKeyFor(db), JSON.stringify(map));
};
const pickHighestUsedBank = (db, allBanks) => {
  const list = (Array.isArray(allBanks) ? allBanks : []).filter(
    (b) => String(b?._id || "") !== CASH_ID,
  );
  if (!list.length) return null;

  const map = readUsage(db);
  let best = null;
  let bestScore = -1;

  for (const b of list) {
    const id = String(b?._id || "");
    const score = Number(map[id]) || 0;
    if (score > bestScore) {
      bestScore = score;
      best = b;
    }
  }
  return best || list[0];
};

const buildParticularOptions = (partyList, bankAccountsForParticular) => {
  const merged = [...(partyList || []), ...(bankAccountsForParticular || [])];

  return merged.map((it) => {
    if (it?.bankAccount === true) {
      const isCash = String(it?._id || "") === CASH_ID || it?.isCash === true;
      return {
        value: `${isCash ? "cash" : "bank"}:${String(it?._id || "")}`,
        label: isCash ? "Cash" : `Bank: ${bankLabel(it)}`,
        searchLabel: isCash
          ? "cash"
          : `bank ${bankLabel(it)}`.trim().toLowerCase(),
        raw: it,
        kind: isCash ? "cash" : "bank",
        id: String(it?._id || ""),
      };
    }

    let kind = "expense";
    if (it?.party) kind = "party";
    else if (it?.User) kind = "user";
    else if (it?.transporter) kind = "transporter";
    else kind = "expense";

    return {
      value: `${kind}:${String(it?._id || "")}`,
      label: it?.fullName || "-",
      searchLabel: String(it?.fullName || "-")
        .trim()
        .toLowerCase(),
      raw: it,
      kind,
      id: String(it?._id || ""),
    };
  });
};

const normalizeHeader = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");

const FORMAT_KEYS = [
  "date",
  "particular",
  "paymentMode",
  "bank",
  "amount",
  "remark",
];

const getBankAddress = (bank) =>
  String(
    bank?.address ||
      bank?.bankAddress ||
      bank?.branchAddress ||
      bank?.bankBranchAddress ||
      "",
  ).trim();

const extractFormatDataRows = (sheet, XLSX) => {
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: true,
  });

  const requiredKeys = FORMAT_KEYS.map(normalizeHeader);
  const headerRowIndex = matrix.findIndex((row) => {
    const rowKeys = (Array.isArray(row) ? row : []).map(normalizeHeader);
    return requiredKeys.every((key) => rowKeys.includes(key));
  });

  if (headerRowIndex < 0) return [];

  return XLSX.utils.sheet_to_json(sheet, {
    range: headerRowIndex,
    defval: "",
    raw: true,
  });
};

const TRANSACTIONS_ACTIVE_TAB = "TRANSACTIONS_ACTIVE_TAB";

/*
  ✅ CREATE PAYMENT RULE
  CASH party + Bank mode => isBankToCash = true
  all other cases => false
*/
const deriveTransferFlags = (line) => {
  const particularType = String(
    line?.particularAccountType || "",
  ).toLowerCase();
  const paymentMode = String(line?.paymentMode || "").toLowerCase();
  const selectedPartySId = String(line?._selectedPartySId || "")
    .trim()
    .toUpperCase();

  const isBankToCash =
    particularType === "party" &&
    selectedPartySId === CASH_PARTY_SID &&
    paymentMode === "bank";

  return {
    isCashToBank: false,
    isBankToCash,
  };
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value?._id || "");
  return String(value);
};

const paymentDataToLine = (data, initialDb) => {
  const iso = fmtDate(data?.date);

  const bankDetailsId =
    (typeof data?.bankDetails === "object" && data?.bankDetails?._id
      ? data.bankDetails._id
      : data?.bankDetails) || "";

  const bankName =
    data?.bankSelect ||
    (typeof data?.bankDetails === "object"
      ? data?.bankDetails?.bankName
      : "") ||
    "";

  const partyId = getEntityId(data?.partyId);
  const userId = getEntityId(data?.userId);
  const transporterId = getEntityId(data?.transporterId);
  const expenseId = getEntityId(data?.expenseId);

  const line = {
    _editId: String(data?._id || ""),
    paymentMode:
      String(bankDetailsId || "").toUpperCase() === CASH_ID
        ? "Cash"
        : data?.paymentMode || "Bank",
    database: data?.database || initialDb,
    type: data?.type || "payment",
    amount: data?.amount ?? "",
    preAmount: data?.amount ?? "",
    date: iso,
    dateText: toDDMMYYYY(iso),
    remark: data?.remark || "",
    bankSelect: bankName,
    bankDetails: bankDetailsId,
    particularValue: "",
    particularAccountType: "",
    _selectedParticularBankName: "",
    _selectedParticularBankId: "",
    _selectedPartySId:
      typeof data?.partyId === "object" ? data?.partyId?.sId || "" : "",
    importedParticularLabel: "",
    isCashToBank: false,
    isBankToCash: Boolean(data?.isBankToCash),
  };

  if (partyId) {
    line.partyId = partyId;
    line.particularValue = `party:${partyId}`;
    line.particularAccountType = "party";
  } else if (userId) {
    line.userId = userId;
    line.particularValue = `user:${userId}`;
    line.particularAccountType = "user";
  } else if (transporterId) {
    line.transporterId = transporterId;
    line.particularValue = `transporter:${transporterId}`;
    line.particularAccountType = "transporter";
  } else if (expenseId) {
    line.expenseId = expenseId;
    line.particularValue = `expense:${expenseId}`;
    line.particularAccountType = "expense";
  }

  return { ...line, ...deriveTransferFlags(line) };
};

export default function CreatePayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const bulkItems = Array.isArray(location.state?.bulkItems)
    ? location.state.bulkItems
    : [];
  const requestedBulkFields = Array.isArray(location.state?.bulkFields)
    ? location.state.bulkFields
    : [];
  const bulkFields = requestedBulkFields.length
    ? requestedBulkFields
    : ["date", "particular", "paymentMode", "amount", "remark"];
  const isBulkEdit = Boolean(location.state?.bulkEdit) && bulkItems.length > 0;
  const isCreate = String(id) === "0" && !isBulkEdit;
  const shouldShowBulkField = (key) =>
    !isBulkEdit ||
    bulkFields.includes(key) ||
    (key === "bankDetails" && bulkFields.includes("paymentMode"));

  const [loader, setLoader] = useState(true);
  const [saving, setSaving] = useState(false);

  const [partyList, setPartyList] = useState([]);
  const [banks, setBanks] = useState([]);
  const [superAdminBankOptions, setSuperAdminBankOptions] = useState([]);

  const [importedFromExcel, setImportedFromExcel] = useState(false);

  const initialDb = JSON.parse(
    localStorage.getItem("userData") || "{}",
  )?.database;

  const draftKey = useMemo(
    () =>
      `CREATE_PAYMENT_DRAFT_${String(id || "0")}_${String(initialDb || "")}`,
    [id, initialDb],
  );

  const [formValues, setFormValues] = useState([
    {
      date: "",
      dateText: "",
      paymentMode: "Bank",
      type: "payment",
      database: initialDb,
      amount: "",
      remark: "",
      bankSelect: "",
      bankDetails: "",
      particularValue: "",
      particularAccountType: "",
      _selectedParticularBankName: "",
      _selectedParticularBankId: "",
      _selectedPartySId: "",
      importedParticularLabel: "",
      isCashToBank: false,
      isBankToCash: false,
    },
  ]);

  const textAreaRefs = useRef({});
  const fileInputRef = useRef(null);

  useEffect(() => injectOnce(), []);

  const autoResizeTextarea = (idx) => {
    const el = textAreaRefs.current[idx];
    if (!el) return;
    el.style.height = "36px";
    el.style.height = `${Math.max(36, el.scrollHeight)}px`;
  };

  useEffect(() => {
    formValues.forEach((_, idx) => autoResizeTextarea(idx));
  }, [formValues]);

  useEffect(() => {
    if (!isCreate || loader) return;
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          formValues,
          importedFromExcel,
        }),
      );
    } catch {}
  }, [formValues, draftKey, importedFromExcel, isCreate, loader]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(draftKey);
    } catch {}
  };

  const getDraft = () => {
    if (!isCreate) return null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.formValues) && parsed.formValues.length) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  };

  const fetchBankData = async () => {
    const me = JSON.parse(localStorage.getItem("userData") || "{}");
    if (!me?._id || !me?.database) return;

    try {
      const res = await ViewCompanyDetails(me?._id, me?.database);
      const list = Array.isArray(res?.CompanyDetail?.bankDetails)
        ? res.CompanyDetail.bankDetails
        : [];

      const cash = {
        _id: CASH_ID,
        bankName: "Cash",
        accountNumber: "",
        isCash: true,
      };

      setBanks([cash, ...list]);
      setSuperAdminBankOptions(
        list.map((b) => ({
          ...b,
          bankAccount: true,
          _id: b?._id,
          bankName: b?.bankName || "Bank",
          accountNumber: b?.accountNumber || "",
          isCash: false,
        })),
      );
    } catch {
      setBanks([{ _id: CASH_ID, bankName: "Cash", isCash: true }]);
      setSuperAdminBankOptions([]);
    }
  };

  const buildPartyListApi = async () => {
    const me = JSON.parse(localStorage.getItem("userData") || "{}");
    const url = `${Create_Account_List}${me?._id}/`;

    const [custRes, accRes, expRes, trRes] = await Promise.allSettled([
      CreateCustomerList(me?._id, me?.database),
      _Get(url, me?.database),
      _Get(View_Expense_Account, me?.database),
      _Get(Create_Transporter_List, me?.database),
    ]);

    const transporters = Array.isArray(trRes?.value?.Transporter)
      ? trRes.value.Transporter.map((t) => ({
          ...t,
          transporter: true,
          fullName: t?.companyName || "-",
        }))
      : [];

    const customers = Array.isArray(custRes?.value?.Customer)
      ? custRes.value.Customer.map((c) => ({
          ...c,
          party: true,
          fullName:
            (c?.CompanyName && c.CompanyName.trim()) ||
            `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
            c?.mobileNumber ||
            "Customer",
        }))
      : [];

    const users = Array.isArray(accRes?.value?.adminDetails)
      ? accRes.value.adminDetails.map((u) => ({
          ...u,
          User: true,
          fullName:
            `${u?.firstName || ""} ${u?.lastName || ""}`.trim() ||
            u?.email ||
            "User",
        }))
      : [];

    const expenses = Array.isArray(expRes?.value?.Expenses)
      ? expRes.value.Expenses.map((e) => ({
          ...e,
          miscellaneous: true,
          fullName:
            [e?.title, e?.type].filter(Boolean).join(" ").trim() || "Expense",
        }))
      : [];

    setPartyList([...customers, ...users, ...expenses, ...transporters]);
  };

  const bankAccountsForParticular = useMemo(() => {
    const profileBanks = (banks || []).map((b) => ({
      ...b,
      bankAccount: true,
      isCash: String(b?._id || "") === CASH_ID || b?.isCash === true,
    }));

    const saBanks = (superAdminBankOptions || []).map((b) => ({
      ...b,
      bankAccount: true,
      isCash: false,
    }));

    return uniqueById([...profileBanks, ...saBanks]);
  }, [banks, superAdminBankOptions]);

  const particularOptions = useMemo(
    () => buildParticularOptions(partyList, bankAccountsForParticular),
    [partyList, bankAccountsForParticular],
  );

  const findParticularOptionByLabel = (label) => {
    const q = String(label || "")
      .trim()
      .toLowerCase();
    if (!q) return null;
    return (
      particularOptions.find((o) => o.searchLabel === q) ||
      particularOptions.find((o) => o.searchLabel.includes(q)) ||
      null
    );
  };

  const findBankByExcelValue = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;

    if (raw.toUpperCase() === CASH_ID || raw.toLowerCase() === "cash") {
      return banks.find((b) => String(b?._id || "") === CASH_ID) || null;
    }

    return (
      banks.find((b) => String(b?._id || "") === raw) ||
      banks.find(
        (b) =>
          String(b?.bankName || "")
            .trim()
            .toLowerCase() === raw.toLowerCase(),
      ) ||
      null
    );
  };

  const loadForEdit = async () => {
    if (isCreate || isBulkEdit) return;

    const res = await _Get(View_Receipt_By_Id, id);
    const d = res?.Receipts || {};
    setFormValues([paymentDataToLine(d, initialDb)]);
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoader(true);

        // Party/customer/user/expense/transporter masters can be large. Start
        // loading them immediately, but do not block the first form paint on
        // the slowest master-data request.
        const partyPromise = buildPartyListApi();

        if (isBulkEdit) {
          setFormValues(
            bulkItems.map((item) => paymentDataToLine(item, initialDb)),
          );
          await fetchBankData();
        } else if (isCreate) {
          const draft = getDraft();
          if (draft?.formValues?.length) {
            setFormValues(draft.formValues);
            setImportedFromExcel(Boolean(draft.importedFromExcel));
          }
          await fetchBankData();
        } else {
          await Promise.all([fetchBankData(), loadForEdit()]);
        }

        if (alive) setLoader(false);
        await partyPromise;
      } catch (e) {
        console.error("[CreatePayment] initialize error:", e);
        if (alive) setLoader(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, isBulkEdit]);

  useEffect(() => {
    if (!isCreate) return;
    if (!banks?.length) return;

    setFormValues((prev) =>
      prev.map((line) => {
        const isBank =
          String(line?.paymentMode || "Bank").toLowerCase() === "bank";

        if (!isBank) return line;
        if (line?.bankSelect && line?.bankDetails) return line;

        const db = line?.database || initialDb;
        const best = pickHighestUsedBank(db, banks);
        if (!best) return line;

        const updated = {
          ...line,
          paymentMode: "Bank",
          bankSelect: best?.bankName || "",
          bankDetails: best?._id || "",
        };

        return { ...updated, ...deriveTransferFlags(updated) };
      }),
    );
  }, [banks, initialDb, isCreate]);

  useEffect(() => {
    if (isCreate) return;
    if (!formValues?.length) return;
    if (!particularOptions?.length) return;

    const bankIdSet = new Set(
      bankAccountsForParticular.map((b) => String(b?._id || "")),
    );

    setFormValues((prev) => {
      if (!prev?.length) return prev;
      let changed = false;

      const nextRows = prev.map((line) => {
        let desired = "";

        if (line?.partyId) desired = `party:${String(line.partyId)}`;
        else if (line?.userId) desired = `user:${String(line.userId)}`;
        else if (line?.transporterId)
          desired = `transporter:${String(line.transporterId)}`;
        else if (line?.expenseId) {
          const expId = String(line.expenseId);
          if (expId === CASH_ID) desired = `cash:${CASH_ID}`;
          else if (bankIdSet.has(expId)) desired = `bank:${expId}`;
          else desired = `expense:${expId}`;
        }

        if (!desired || desired === line.particularValue) return line;

        changed = true;
        const kind = desired.split(":")[0];
        let next = {
          ...line,
          particularValue: desired,
          particularAccountType: kind,
        };

        if (kind === "bank" || kind === "cash") {
          const selectedId = desired.split(":")[1];
          const raw = bankAccountsForParticular.find(
            (b) => String(b?._id || "") === String(selectedId),
          );
          next._selectedParticularBankId = String(selectedId);
          next._selectedParticularBankName =
            kind === "cash" ? "Cash" : raw?.bankName || "";
        } else {
          next._selectedParticularBankId = "";
          next._selectedParticularBankName = "";
        }

        return { ...next, ...deriveTransferFlags(next) };
      });

      return changed ? nextRows : prev;
    });
  }, [isCreate, isBulkEdit, particularOptions, bankAccountsForParticular]);

  const applyParticularSelection = (idx, valueStr) => {
    setFormValues((prev) => {
      const next = [...prev];
      const line = { ...next[idx] };

      line.particularValue = valueStr;

      delete line.partyId;
      delete line.userId;
      delete line.transporterId;
      delete line.expenseId;

      line.particularAccountType = "";
      line._selectedParticularBankName = "";
      line._selectedParticularBankId = "";
      line._selectedPartySId = "";

      if (!valueStr) {
        next[idx] = { ...line, ...deriveTransferFlags(line) };
        return next;
      }

      const found = particularOptions.find((o) => o.value === valueStr);
      const raw = found?.raw;
      const kind = found?.kind;

      line.particularAccountType = kind;

      if (kind === "party") {
        line.partyId = found.id;
        line._selectedPartySId = raw?.sId || "";
      } else if (kind === "user") {
        line.userId = found.id;
      } else if (kind === "transporter") {
        line.transporterId = found.id;
      } else if (kind === "expense") {
        line.expenseId = found.id;
      } else if (kind === "bank") {
        line.expenseId = found.id;
        line._selectedParticularBankId = String(found.id);
        line._selectedParticularBankName = raw?.bankName || "";
      } else if (kind === "cash") {
        line.expenseId = CASH_ID;
        line._selectedParticularBankId = CASH_ID;
        line._selectedParticularBankName = "Cash";
      }

      next[idx] = { ...line, ...deriveTransferFlags(line) };
      return next;
    });
  };

  const onChangeLine = (i, e) => {
    const { name, value } = e.target;

    setFormValues((prev) => {
      const next = [...prev];
      const line = { ...next[i] };

      if (name === "amount") {
        line.amount = value;
      } else if (name === "paymentMode") {
        line.paymentMode = value || "Bank";

        if (String(line.paymentMode).toLowerCase() === "cash") {
          line.bankDetails = CASH_ID;
          line.bankSelect = "Cash";
        } else {
          if (!line.bankDetails || String(line.bankDetails) === CASH_ID) {
            const db = line?.database || initialDb;
            const best = pickHighestUsedBank(db, banks);
            if (best) {
              line.bankDetails = best?._id || "";
              line.bankSelect = best?.bankName || "";
            } else {
              line.bankDetails = "";
              line.bankSelect = "";
            }
          }
        }
      } else if (name === "bankDetails") {
        const id = value || "";
        line.bankDetails = id;

        if (!id) {
          line.bankSelect = "";
        } else if (String(id) === CASH_ID) {
          line.bankSelect = "Cash";
          line.paymentMode = "Cash";
        } else {
          const matched = banks.find(
            (b) => String(b?._id || "") === String(id),
          );
          line.bankSelect = matched?.bankName || line.bankSelect || "";
          line.paymentMode = "Bank";
        }
      } else if (name === "dateText") {
        line.dateText = value;
        const normalized = normalizeDateInputToDDMMYYYY(value);
        const iso = ddmmyyyyToISO(normalized);
        if (iso) {
          line.date = iso;
          line.dateText = normalized;
        }
      } else if (name === "remark") {
        line.remark = value;
      } else {
        line[name] = value;
      }

      next[i] = { ...line, ...deriveTransferFlags(line) };
      return next;
    });
  };

  const addFormLine = () => {
    setFormValues((prev) => {
      const db = initialDb;
      const best = pickHighestUsedBank(db, banks);
      const lastLine = prev?.[prev.length - 1] || {};

      const line = {
        date: lastLine?.date || "",
        dateText: lastLine?.dateText || "",
        paymentMode: "Bank",
        type: "payment",
        database: db,
        amount: "",
        remark: "",
        bankSelect: best?.bankName || "",
        bankDetails: best?._id || "",
        particularValue: "",
        particularAccountType: "",
        _selectedParticularBankName: "",
        _selectedParticularBankId: "",
        _selectedPartySId: "",
        importedParticularLabel: "",
        isCashToBank: false,
        isBankToCash: false,
      };

      return [...prev, { ...line, ...deriveTransferFlags(line) }];
    });
  };

  const removeFormLine = (idx) => {
    setFormValues((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    for (const v of formValues) {
      if ((!isBulkEdit || bulkFields.includes("date")) && !notEmpty(v.date))
        return "Date is required";

      if (
        (!isBulkEdit || bulkFields.includes("paymentMode")) &&
        !notEmpty(v.paymentMode)
      )
        return "Payment Mode is required";

      if (
        (!isBulkEdit || bulkFields.includes("paymentMode")) &&
        (!notEmpty(v.bankDetails) || !notEmpty(v.bankSelect))
      )
        return "Please select a valid Bank/Cash (To Account)";

      if (
        (!isBulkEdit || bulkFields.includes("particular")) &&
        !(v.partyId || v.userId || v.expenseId || v.transporterId)
      ) {
        return `Please select a valid Particular${
          v.importedParticularLabel ? ` for "${v.importedParticularLabel}"` : ""
        }`;
      }

      if (
        (!isBulkEdit || bulkFields.includes("amount")) &&
        (!v.amount || Number(v.amount) <= 0)
      )
        return "Amount must be > 0";
    }
    return "";
  };

  const buildBulkPaymentPayload = (line, selectedDatabase) => {
    const computed = {
      ...line,
      ...deriveTransferFlags(line),
      database: selectedDatabase,
    };

    const data = { database: selectedDatabase };

    if (bulkFields.includes("date")) {
      data.date = computed.date;
      data.financialYear =
        computed.financialYear || getFinancialYearLabel(computed.date);
    }

    if (bulkFields.includes("particular")) {
      data.partyId = computed.partyId || null;
      data.userId = computed.userId || null;
      data.transporterId = computed.transporterId || null;
      data.expenseId = computed.expenseId || null;
      data.isCashToBank = computed.isCashToBank;
      data.isBankToCash = computed.isBankToCash;
    }

    if (bulkFields.includes("paymentMode")) {
      data.paymentMode = computed.paymentMode;
      data.bankDetails = computed.bankDetails || null;
      data.bankSelect = computed.bankSelect || "";
      data.isCashToBank = computed.isCashToBank;
      data.isBankToCash = computed.isBankToCash;
    }

    if (bulkFields.includes("amount")) {
      data.amount = Number(computed.amount);
      data.preAmount = Number(computed.preAmount ?? computed.amount);
    }

    if (bulkFields.includes("remark")) {
      data.remark = computed.remark || "";
    }

    return data;
  };

  const handleDownloadFormat = async () => {
    try {
      const xlsxModule = await import("xlsx");
      const XLSX = xlsxModule.default || xlsxModule;
      const availableBanks = (banks || []).filter(
        (bank, index, list) =>
          bank?._id &&
          list.findIndex(
            (item) => String(item?._id || "") === String(bank?._id || ""),
          ) === index,
      );

      const preferredBank = availableBanks.find(
        (bank) => String(bank?._id || "") !== CASH_ID,
      );

      const bankRows = availableBanks.map((bank) => [
        bank?.bankName || (String(bank?._id || "") === CASH_ID ? "Cash" : ""),
        bank?.branchName || "",
        getBankAddress(bank),
        bank?.accountNumber || "",
        String(bank?._id || ""),
        "",
      ]);

      const sheetRows = [
        ["CREATE PAYMENT - BULK UPLOAD FORMAT", "", "", "", "", ""],
        [],
        ["INSTRUCTIONS", "", "", "", "", ""],
        ["KEY NAME", "HOW TO FILL", "", "", "", ""],
        [
          "date",
          "Use DD-MM-YYYY, DD-MMM-YYYY, or a full month date such as 06 April 2026.",
        ],
        ["particular", "Enter the exact Particular name shown in the system."],
        ["paymentMode", "Enter Bank or Cash."],
        [
          "bank",
          "For Bank mode, use the bank _id or exact bank name listed below. For Cash mode, use CASH.",
        ],
        ["amount", "Enter numbers only. The amount must be greater than zero."],
        ["remark", "Optional text."],
        [
          "IMPORTANT",
          "Do not rename the key columns. Replace the sample row or add your rows below it.",
        ],
        [],
        ["BANK ACCOUNT DETAILS", "", "", "", "", ""],
        ["Bank Name", "Branch Name", "Address", "Account Number", "_id", ""],
        ...bankRows,
        [],
        ["DATA ENTRY - KEEP THESE KEY NAMES UNCHANGED", "", "", "", "", ""],
        FORMAT_KEYS,
        [
          "06-04-2026",
          "NAME",
          "Bank",
          String(preferredBank?._id || CASH_ID),
          1000,
          "Payment remark",
        ],
      ];

      const ws = XLSX.utils.aoa_to_sheet(sheetRows);
      ws["!cols"] = [
        { wch: 22 },
        { wch: 76 },
        { wch: 20 },
        { wch: 32 },
        { wch: 30 },
        { wch: 40 },
      ];
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
        { s: { r: 12, c: 0 }, e: { r: 12, c: 5 } },
        {
          s: { r: 15 + bankRows.length, c: 0 },
          e: { r: 15 + bankRows.length, c: 5 },
        },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payment Format");
      XLSX.writeFile(wb, "create-payment-format.xlsx");
    } catch (err) {
      console.error(err);
      swal("Unable to download format");
    }
  };

  const convertExcelRowsToFormLines = (rows) => {
    const best = pickHighestUsedBank(initialDb, banks);

    return rows
      .map((row) => {
        const normalized = {};
        Object.keys(row || {}).forEach((k) => {
          normalized[normalizeHeader(k)] = row[k];
        });

        const dateText = normalizeAnyExcelDateToDDMMYYYY(normalized.date);
        const date = ddmmyyyyToISO(dateText);

        const importedParticularLabel = String(
          normalized.particular || "",
        ).trim();

        const matchedParticular = findParticularOptionByLabel(
          importedParticularLabel,
        );

        const paymentMode =
          String(normalized.paymentmode || "Bank")
            .trim()
            .toLowerCase() === "cash"
            ? "Cash"
            : "Bank";

        const bankMatch =
          findBankByExcelValue(normalized.bank) ||
          (paymentMode === "Bank"
            ? best
            : banks.find((b) => String(b?._id || "") === CASH_ID) || null);

        const line = {
          date: date || "",
          dateText: dateText || "",
          paymentMode,
          type: "payment",
          database: initialDb,
          amount:
            normalized.amount !== undefined && normalized.amount !== null
              ? String(normalized.amount)
              : "",
          remark: String(normalized.remark || "").trim(),
          bankSelect:
            paymentMode === "Cash" ? "Cash" : bankMatch?.bankName || "",
          bankDetails: paymentMode === "Cash" ? CASH_ID : bankMatch?._id || "",
          particularValue: matchedParticular?.value || "",
          particularAccountType: matchedParticular?.kind || "",
          _selectedParticularBankName:
            matchedParticular?.kind === "bank"
              ? matchedParticular?.raw?.bankName || ""
              : matchedParticular?.kind === "cash"
                ? "Cash"
                : "",
          _selectedParticularBankId:
            matchedParticular?.kind === "bank" ||
            matchedParticular?.kind === "cash"
              ? matchedParticular?.id || ""
              : "",
          _selectedPartySId:
            matchedParticular?.kind === "party"
              ? matchedParticular?.raw?.sId || ""
              : "",
          importedParticularLabel,
          isCashToBank: false,
          isBankToCash: false,
        };

        if (matchedParticular) {
          if (matchedParticular.kind === "party")
            line.partyId = matchedParticular.id;
          else if (matchedParticular.kind === "user")
            line.userId = matchedParticular.id;
          else if (matchedParticular.kind === "transporter")
            line.transporterId = matchedParticular.id;
          else if (matchedParticular.kind === "expense")
            line.expenseId = matchedParticular.id;
          else if (matchedParticular.kind === "bank")
            line.expenseId = matchedParticular.id;
          else if (matchedParticular.kind === "cash") line.expenseId = CASH_ID;
        }

        return { ...line, ...deriveTransferFlags(line) };
      })
      .filter((line) => {
        return (
          line.dateText ||
          line.importedParticularLabel ||
          line.amount ||
          line.remark
        );
      });
  };

  const handleImportExcel = async (file) => {
    if (!file) return;

    try {
      const xlsxModule = await import("xlsx");
      const XLSX = xlsxModule.default || xlsxModule;
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, {
        type: "array",
        cellDates: true,
      });

      const sheetName = workbook.SheetNames?.[0];
      if (!sheetName) {
        swal("No sheet found in uploaded file");
        return;
      }

      const sheet = workbook.Sheets[sheetName];
      const rows = extractFormatDataRows(sheet, XLSX);

      if (!rows.length) {
        swal(
          "Could not find the payment key columns: date, particular, paymentMode, bank, amount, remark",
        );
        return;
      }

      const lines = convertExcelRowsToFormLines(rows);

      if (!lines.length) {
        swal("No valid payment rows found in uploaded file");
        return;
      }

      setFormValues(lines);
      setImportedFromExcel(true);

      const invalidDates = lines.filter(
        (l) => l.dateText && !/^\d{2}-\d{2}-\d{4}$/.test(l.dateText),
      ).length;

      const unresolved = lines.filter(
        (l) =>
          l.importedParticularLabel &&
          !(l.partyId || l.userId || l.expenseId || l.transporterId),
      ).length;

      if (invalidDates > 0) {
        swal(
          `Imported ${lines.length} rows. ${invalidDates} date(s) could not be auto-converted. Please correct them manually.`,
        );
      } else if (unresolved > 0) {
        swal(
          `Imported ${lines.length} rows. ${unresolved} particular(s) need manual selection.`,
        );
      } else {
        swal(
          `Imported ${lines.length} rows successfully. You can review and edit before submit.`,
        );
      }
    } catch (err) {
      console.error(err);
      swal("Unable to read xlsx file");
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const financialYearSelected = localStorage.getItem(
        "financialYearSelected",
      );

      const selectedDatabase = financialYearSelected
        ? `${user?.database || initialDb}-${financialYearSelected}`
        : user?.database || initialDb;

      const msg = validate();
      if (msg) {
        swal(msg);
        setSaving(false);
        return;
      }

      try {
        const db = initialDb;
        formValues.forEach((l) => {
          if (
            String(l?.bankDetails || "") &&
            String(l?.bankDetails || "") !== CASH_ID
          ) {
            bumpUsage(db, l.bankDetails);
          }
        });
      } catch {}

      if (isBulkEdit) {
        await Promise.all(
          formValues.map((line) => {
            const rowId = String(line?._editId || "");
            if (!rowId) {
              throw new Error("Missing payment id in bulk edit row");
            }

            const cleaned = buildBulkPaymentPayload(line, selectedDatabase);

            return _Put(Update_payment_By_Id2, rowId, cleaned);
          }),
        );

        try {
          sessionStorage.setItem(TRANSACTIONS_ACTIVE_TAB, "payment");
        } catch {}
        swal(`${formValues.length} Payment(s) Updated Successfully`);
      } else if (isCreate) {
        const payload = {
          Payment: formValues.map((line) => {
            const computed = {
              ...line,
              ...deriveTransferFlags(line),
              database: selectedDatabase,
            };
            const cleaned = {
              ...computed,
              amount: Number(computed.amount),
              bankDetails: computed?.bankDetails || undefined,
              financialYear:
                computed?.financialYear ||
                getFinancialYearLabel(computed?.date),
            };
            delete cleaned._editId;
            delete cleaned.importedParticularLabel;
            delete cleaned._selectedParticularBankName;
            delete cleaned._selectedParticularBankId;
            delete cleaned._selectedPartySId;
            delete cleaned.particularAccountType;
            return cleaned;
          }),
        };

        await _PostSave(Create_Payment, payload);
        clearDraft();
        setImportedFromExcel(false);
        try {
          sessionStorage.setItem(TRANSACTIONS_ACTIVE_TAB, "payment");
        } catch {}
        swal("Added successfully");
      } else {
        const computed = {
          ...formValues[0],
          ...deriveTransferFlags(formValues[0]),
          database: selectedDatabase,
        };
        const cleaned = {
          ...computed,
          amount: Number(computed?.amount),
          bankDetails: computed?.bankDetails || undefined,
          financialYear:
            computed?.financialYear || getFinancialYearLabel(computed?.date),
        };
        delete cleaned._editId;
        delete cleaned.importedParticularLabel;
        delete cleaned._selectedParticularBankName;
        delete cleaned._selectedParticularBankId;
        delete cleaned._selectedPartySId;
        delete cleaned.particularAccountType;

        await _Put(Update_payment_By_Id2, id, cleaned);
        try {
          sessionStorage.setItem(TRANSACTIONS_ACTIVE_TAB, "payment");
        } catch {}
        swal("Updated successfully");
      }

      navigate(-1);
    } catch (err) {
      console.error(err);
      swal("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (loader) {
    return (
      <div className="d-flex justify-content-center align-item-center mt-3">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="cp-wrap">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0] || null;
          await handleImportExcel(file);
          e.target.value = "";
        }}
      />

      <Card className="cp-card">
        <div className="cp-toolbar">
          <div className="cp-title">
            {isBulkEdit
              ? `Bulk Edit Payment (${formValues.length})`
              : isCreate
                ? "Create Payment"
                : "Edit Payment"}
          </div>

          <div className="cp-actions">
            {isCreate && (
              <Button color="primary" type="button" onClick={addFormLine}>
                + Add Line
              </Button>
            )}

            {isCreate && (
              <Button color="info" type="button" onClick={handleDownloadFormat}>
                Download Format
              </Button>
            )}

            {isCreate && (
              <Button
                color="primary"
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                Bulk Upload
              </Button>
            )}

            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>

        <CardBody style={{ padding: 12 }}>
          {isBulkEdit && (
            <div className="cp-import-note">
              You are editing {formValues.length} selected payment(s). Only
              these selected columns are editable:{" "}
              <b>{bulkFields.join(", ")}</b>. Payment Mode also shows Bank/Cash
              because that account is required when changing the payment mode.
              Unselected fields are not sent in the bulk update.
            </div>
          )}

          {importedFromExcel && isCreate && (
            <div className="cp-import-note">
              Excel rows are loaded into the form. Dates like{" "}
              <b>06 April 2026</b>
              are auto-converted to <b>06-04-2026</b>. Review and edit the
              Particular field if needed, then click Submit to save.
            </div>
          )}

          <Form className="cp-form" onSubmit={submitHandler}>
            {formValues.map((line, idx) => {
              const unresolvedParticular =
                line.importedParticularLabel &&
                !(
                  line.partyId ||
                  line.userId ||
                  line.expenseId ||
                  line.transporterId
                );

              const hasCurrentParticular =
                !line.particularValue ||
                particularOptions.some((o) => o.value === line.particularValue);

              const fallbackParticularLabel = line.particularValue?.startsWith(
                "bank:",
              )
                ? `Bank: ${line._selectedParticularBankName || "Bank"}`
                : line.particularValue?.startsWith("cash:")
                  ? "Cash"
                  : "Selected";

              return (
                <div key={idx}>
                  <div
                    className={isBulkEdit ? "cp-line cp-line-bulk" : "cp-line"}
                  >
                    {shouldShowBulkField("date") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Date *</Label>
                        <Input
                          type="text"
                          name="dateText"
                          value={line.dateText || line.date || ""}
                          placeholder="DD-MM-YYYY"
                          onChange={(e) => onChangeLine(idx, e)}
                        />
                      </FormGroup>
                    )}

                    {shouldShowBulkField("particular") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Particular *</Label>
                        <Input
                          type="select"
                          name="particularValue"
                          value={line.particularValue || ""}
                          onChange={(e) =>
                            applyParticularSelection(idx, e.target.value)
                          }
                        >
                          <option value="">
                            {unresolvedParticular
                              ? `Imported: ${line.importedParticularLabel} (select exact match)`
                              : "--Select--"}
                          </option>

                          {!hasCurrentParticular && line.particularValue && (
                            <option value={line.particularValue}>
                              {fallbackParticularLabel}
                            </option>
                          )}

                          {particularOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Input>

                        {unresolvedParticular && (
                          <div className="cp-note">
                            Imported Particular:{" "}
                            <b>{line.importedParticularLabel}</b>. Select the
                            correct system record before submit.
                          </div>
                        )}
                      </FormGroup>
                    )}

                    {shouldShowBulkField("paymentMode") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Payment Mode *</Label>
                        <Input
                          type="select"
                          name="paymentMode"
                          value={line.paymentMode || "Bank"}
                          onChange={(e) => onChangeLine(idx, e)}
                        >
                          <option value="Bank">Bank</option>
                          <option value="Cash">Cash</option>
                        </Input>
                      </FormGroup>
                    )}

                    {shouldShowBulkField("bankDetails") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Bank/Cash (To) *</Label>
                        <Input
                          type="select"
                          name="bankDetails"
                          value={line.bankDetails || ""}
                          onChange={(e) => onChangeLine(idx, e)}
                          disabled={
                            String(line.paymentMode || "").toLowerCase() ===
                            "cash"
                          }
                        >
                          <option value="">--Select--</option>
                          {banks.map((b) => (
                            <option key={b?._id || b?.bankName} value={b?._id}>
                              {String(b?._id || "") === CASH_ID
                                ? "Cash"
                                : bankLabel(b)}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    )}

                    {shouldShowBulkField("amount") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Amount *</Label>
                        <Input
                          required
                          type="number"
                          inputMode="decimal"
                          className="cp-amount"
                          name="amount"
                          placeholder="Amount"
                          value={line.amount || ""}
                          onChange={(e) => onChangeLine(idx, e)}
                        />
                      </FormGroup>
                    )}

                    {shouldShowBulkField("remark") && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>Remarks</Label>
                        <Input
                          innerRef={(el) => {
                            textAreaRefs.current[idx] = el;
                          }}
                          type="textarea"
                          name="remark"
                          placeholder="Remark here"
                          value={line.remark || ""}
                          onChange={(e) => {
                            onChangeLine(idx, e);
                            setTimeout(() => autoResizeTextarea(idx), 0);
                          }}
                        />
                      </FormGroup>
                    )}

                    {!isBulkEdit && (
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label>&nbsp;</Label>
                        <Button
                          type="button"
                          className="cp-xbtn"
                          title="Remove"
                          onClick={() => removeFormLine(idx)}
                          disabled={!isCreate || idx === 0}
                        >
                          ×
                        </Button>
                      </FormGroup>
                    )}
                  </div>

                  <div className="cp-sep" />
                </div>
              );
            })}

            <div className="d-flex justify-content-center">
              <Button
                color="primary"
                type="submit"
                disabled={saving}
                className="mt-2"
              >
                {saving
                  ? isBulkEdit || !isCreate
                    ? "Updating..."
                    : "Submitting..."
                  : "Submit"}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}
